// sb-store.js (builder B1): small behaviours of the source's chrome, rebuilt (no app or theme code copied).
//  - menu drawer: the "Log in" row under the links (as on the source)
//  - back-to-top button: visible after 400px, smooth scroll to the top
//  - "Try Your Luck" scratch pop-up (phones): scratch -> claim -> email (Shopify customer form) -> code
//  - quick view trigger script for the SEO collection's single-product layout (kit's sb-quick-view.js)
//  - product pages of the theme-built templates: offers (deal / quantity breaks) + add to cart, sticky bar (FIX-C)
//  - scratch pop-up SHOP NOW: the code joins the cart's codes

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const store = {
  get(key) { try { return localStorage.getItem(key); } catch { return null; } },
  set(key, value) { try { localStorage.setItem(key, value); } catch {} },
};

/* ---------- menu drawer: Log in row ---------- */
function addDrawerAccount() {
  // only when the store has customer accounts switched on (Settings > Customer accounts); off = no Log in row
  if (!window.sbAccounts) return;
  const links = document.querySelector('.header-drawer .menu-drawer__utility-links');
  if (!links || links.querySelector('.sb-drawer-account, .menu-drawer__account')) return;
  const a = document.createElement('a');
  a.className = 'sb-drawer-account';
  a.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'account/login';
  a.innerHTML = '<svg viewBox="0 0 18 19" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M6 4.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0Zm3-4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm5.58 12.15c1.12.82 1.83 2.24 1.91 4.85H1.51c.08-2.6.79-4.03 1.9-4.85 1.25-.9 3.09-1.15 5.59-1.15s4.35.26 5.58 1.15ZM9 10.5c-2.5 0-4.65.24-6.17 1.35C1.27 12.98.5 14.93.5 18v.5h17V18c0-3.07-.77-5.02-2.33-6.15-1.52-1.1-3.67-1.35-6.17-1.35Z" fill="currentColor"/></svg><span>Log in</span>';
  links.prepend(a);
}

/* ---------- back to top ---------- */
function initTop() {
  const btn = document.querySelector('[data-sb-top]');
  if (!btn) return;
  const sync = () => btn.classList.toggle('is-visible', window.scrollY > 400);
  sync();
  window.addEventListener('scroll', sync, { passive: true });
  btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
}

/* ---------- scratch pop-up ---------- */
function drawFoil(canvas, label) {
  const rect = canvas.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.max(1, Math.round(rect.width * dpr));
  canvas.height = Math.max(1, Math.round(rect.height * dpr));
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const w = rect.width, h = rect.height;
  const g = ctx.createLinearGradient(0, 0, w * 0.35, h * 1.1);
  g.addColorStop(0, '#f0cf73');
  g.addColorStop(0.35, '#d8b354');
  g.addColorStop(0.7, '#c7a245');
  g.addColorStop(1, '#a8862f');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  // foil grain
  for (let i = 0; i < 1400; i++) {
    ctx.fillStyle = `rgba(${Math.random() < 0.5 ? '255,240,190' : '120,90,20'},${Math.random() * 0.12})`;
    ctx.fillRect(Math.random() * w, Math.random() * h, 1.5, 1.5);
  }
  // scratch marks: the source's brush scribble behind the label, five thick rough strokes leaning right, fanning out
  // left to right; drawn in card fractions so they scale with the card (source card 300 x 219 px)
  const bars = [[58, 146, 84, 58, 26], [97, 154, 142, 58, 30], [140, 152, 189, 60, 30], [184, 162, 223, 72, 28], [221, 182, 249, 78, 25]];
  let seed = 11;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const sx = w / 300, sy = h / 219;
  ctx.save();
  for (const [x1, y1, x2, y2, bw0] of bars) {
    const ax = x1 * sx, ay = y1 * sy, bx = x2 * sx, by = y2 * sy, bw = bw0 * sx;
    const len = Math.hypot(bx - ax, by - ay), ux = (bx - ax) / len, uy = (by - ay) / len, nx = -uy, ny = ux;
    const steps = Math.max(8, Math.round(len / 4));
    const left = [], right = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps, px = ax + (bx - ax) * t, py = ay + (by - ay) * t;
      const hw = bw / 2 * (0.9 + 0.1 * Math.sin(t * Math.PI));
      left.push([px + nx * (hw + (rnd() - 0.5) * 3), py + ny * (hw + (rnd() - 0.5) * 3)]);
      right.push([px - nx * (hw + (rnd() - 0.5) * 3), py - ny * (hw + (rnd() - 0.5) * 3)]);
    }
    ctx.beginPath();
    left.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    right.reverse().forEach(([x, y]) => ctx.lineTo(x, y));
    ctx.closePath();
    ctx.fillStyle = 'rgba(112, 88, 22, 0.30)';
    ctx.fill();
    // dry-brush grain inside the stroke: short light and dark streaks along it
    ctx.clip();
    for (let k = 0; k < 18; k++) {
      const off = (rnd() - 0.5) * bw, t0 = rnd() * 0.8, t1 = Math.min(1, t0 + 0.1 + rnd() * 0.4);
      ctx.strokeStyle = `rgba(${rnd() < 0.4 ? '225, 195, 110' : '90, 68, 14'}, ${0.03 + rnd() * 0.05})`;
      ctx.lineWidth = 1 + rnd() * 2;
      ctx.beginPath();
      ctx.moveTo(ax + (bx - ax) * t0 + nx * off, ay + (by - ay) * t0 + ny * off);
      ctx.lineTo(ax + (bx - ax) * t1 + nx * off, ay + (by - ay) * t1 + ny * off);
      ctx.stroke();
    }
    ctx.restore();
    ctx.save();
  }
  ctx.restore();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.62)';
  ctx.font = `600 ${Math.round(w * 0.105)}px Poppins, Montserrat, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label || 'SCRATCH HERE', w / 2, h / 2);
}

function initScratch() {
  const root = document.getElementById('SbScratch');
  if (!root) return;
  const posted = !!root.querySelector('[data-sb-scratch-posted]');
  const days = Number(root.dataset.days || 30);
  const closedAt = Number(store.get('sb-scratch-closed') || 0);
  const isPhone = window.matchMedia('(max-width: 749px)').matches;
  const steps = [...root.querySelectorAll('[data-step]')];
  const show = (name) => steps.forEach((s) => { s.hidden = s.dataset.step !== name; });
  const close = () => {
    root.hidden = true;
    document.documentElement.classList.remove('sb-scratch-open');
    store.set('sb-scratch-closed', String(Date.now()));
  };
  root.querySelectorAll('[data-sb-scratch-close]').forEach((b) => b.addEventListener('click', close));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !root.hidden) close(); });

  const open = (step) => {
    root.hidden = false;
    document.documentElement.classList.add('sb-scratch-open');
    show(step);
    if (step === 'scratch') setupCanvas();
  };

  // back from the email form: reveal the code (also when the form had errors: stay on the form)
  if (posted) { open('code'); store.set('sb-scratch-claimed', '1'); return; }
  if (root.querySelector('.sb-scratch__err')) { open('claim'); return; }
  if (!isPhone || store.get('sb-scratch-claimed')) return;
  if (closedAt && Date.now() - closedAt < days * 864e5) return;

  // (the claim step's 30-second per-visitor timer was cut in the Google Merchant Center clean-up, 2026-10-23)
  let canvasReady = false;
  function setupCanvas() {
    if (canvasReady) return;
    const canvas = root.querySelector('.sb-scratch__foil');
    if (!canvas) return;
    canvasReady = true;
    drawFoil(canvas, canvas.getAttribute('aria-label'));
    const ctx = canvas.getContext('2d');
    let down = false;
    let done = false;
    const claim = root.querySelector('[data-sb-scratch-claim]');
    const scratchAt = (e) => {
      const r = canvas.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      ctx.globalCompositeOperation = 'destination-out';
      // an opaque brush: the label's half-transparent white left the foil at alpha ~2, never fully clear, so the
      // card never counted as scratched
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.arc(p.clientX - r.left, p.clientY - r.top, Math.max(18, r.width * 0.07), 0, Math.PI * 2);
      ctx.fill();
    };
    const cleared = () => {
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let clear = 0;
      for (let i = 3; i < data.length; i += 64) if (data[i] === 0) clear++;
      return clear / (data.length / 64);
    };
    const finish = () => {
      if (done) return;
      done = true;
      canvas.classList.add('is-done');
      root.classList.add('is-scratched');
      if (claim) claim.hidden = false;
    };
    const start = (e) => { down = true; scratchAt(e); };
    const move = (e) => { if (!down) return; if (e.cancelable) e.preventDefault(); scratchAt(e); };
    const end = () => { if (!down) return; down = false; if (cleared() > 0.35) finish(); };
    canvas.addEventListener('pointerdown', (e) => { canvas.setPointerCapture?.(e.pointerId); start(e); });
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    canvas.addEventListener('touchmove', (e) => { if (e.cancelable) e.preventDefault(); }, { passive: false });
    // a tap without scratching also works (keyboard / accessibility): Enter or Space reveals
    canvas.tabIndex = 0;
    canvas.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); finish(); } });
    claim?.addEventListener('click', () => show('claim'));
  }

  const delay = Number(root.dataset.delay || 3) * 1000;
  setTimeout(() => open('scratch'), delay);
}

/* ---------- search page: predictive suggestions + clear button ---------- */
function initPredictive() {
  const form = document.querySelector('[data-sb-predictive]');
  if (!form) return;
  const input = form.querySelector('input[name="q"]');
  const box = form.querySelector('.sb-search__suggest');
  const reset = form.querySelector('.sb-search__reset');
  if (!input || !box) return;
  let timer = null;
  let ctrl = null;
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const hide = () => { box.hidden = true; input.setAttribute('aria-expanded', 'false'); };
  const render = (q, data) => {
    const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
    if (!q) return hide();
    let html = '<p class="sb-search__suggest-head">Products</p>';
    html += products.length
      ? products.map((p) => {
          const img = p.featured_image && p.featured_image.url ? `<img src="${esc(p.featured_image.url)}${p.featured_image.url.includes('?') ? '&' : '?'}width=100" alt="" width="50" height="50">` : '';
          return `<a href="${esc(p.url)}" role="option">${img}<span>${esc(p.title)}</span></a>`;
        }).join('')
      : '<p class="sb-search__suggest-head" style="text-transform:none">No results</p>';
    html += `<a class="sb-search__all" href="${form.getAttribute('action')}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last"><span>Search for “${esc(q)}”</span><span aria-hidden="true">&rarr;</span></a>`;
    box.innerHTML = html;
    box.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };
  input.addEventListener('input', () => {
    const q = input.value.trim();
    if (reset) reset.classList.toggle('is-hidden', !input.value);
    clearTimeout(timer);
    if (!q) return hide();
    timer = setTimeout(async () => {
      try {
        if (ctrl) ctrl.abort();
        ctrl = new AbortController();
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const r = await fetch(`${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last`, { signal: ctrl.signal });
        render(q, await r.json());
      } catch (e) { if (e.name !== 'AbortError') hide(); }
    }, 250);
  });
  if (reset) reset.addEventListener('click', () => { setTimeout(() => { input.value = ''; reset.classList.add('is-hidden'); hide(); input.focus(); }, 0); });
  document.addEventListener('click', (e) => { if (!form.contains(e.target)) hide(); });
  input.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });
}

/* ---------- product page: quantity-break cards set the add-to-cart quantity ---------- */
function syncQtyBreak(card) {
  const box = card.closest('[data-sb-qb]');
  if (!box) return;
  box.querySelectorAll('.sb-qb__tier').forEach((t) => t.classList.toggle('is-selected', t === card));
  const scope = box.closest('.product-details, product-info, [data-product-grid-content], .shopify-section') || document;
  const qty = scope.querySelector('input[name="quantity"]');
  if (qty && qty.value !== (card.dataset.qty || '1')) {
    qty.value = card.dataset.qty || '1';
    qty.dispatchEvent(new Event('input', { bubbles: true }));
    qty.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
function initQtyBreaks() {
  document.addEventListener('change', (e) => {
    const input = e.target instanceof HTMLInputElement ? e.target : null;
    if (input && input.closest('[data-sb-qb]')) syncQtyBreak(input.closest('.sb-qb__tier'));
  });
  // the pre-selected card's quantity applies on load (and after Horizon re-renders the section on a variant change)
  const apply = () => document.querySelectorAll('[data-sb-qb] .sb-qb__tier.is-selected').forEach(syncQtyBreak);
  if (!document.querySelector('[data-sb-qb]')) return;
  apply();
  new MutationObserver(() => { clearTimeout(apply.t); apply.t = setTimeout(apply, 100); }).observe(document.body, { childList: true, subtree: true });
}

/* ---------- media slider: dots + play buttons ---------- */
function initSliders() {
  document.querySelectorAll('[data-sb-slider]').forEach((track) => {
    const dots = track.parentElement.querySelectorAll('.sb-sl__dot');
    track.addEventListener('scroll', () => {
      const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
    }, { passive: true });
  });
  document.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-sb-play]') : null;
    if (!btn) return;
    const slide = btn.closest('.sb-sl__slide');
    const video = slide && slide.querySelector('video');
    if (!video) return;
    document.querySelectorAll('.sb-sl__slide.is-playing video').forEach((v) => { if (v !== video) { v.pause(); v.closest('.sb-sl__slide').classList.remove('is-playing'); } });
    video.controls = true;
    video.play().catch(() => {});
    slide.classList.add('is-playing');
  });
}

/* ---------- product page (theme-built templates): offers, add to cart, sticky bar ---------- */
// One ADD TO CART per page (Horizon's buy-buttons). It adds the shopper's last-picked offer: the deal block
// (blocks/sb-store-deal: G2's Kaching rebuild, added by assets/sb-gp-product.js through its hidden button) or a
// quantity-break tier with a code / per-unit selects (added here: units + the tier's codes merged with the cart's
// own codes, so the cart charges the price shown). Plain quantity breaks only set the form's quantity (Horizon adds).
const MAX_CODES = 5;
const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const shopCurrency = () => (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
let moneyFmt = '${{amount}}';
const fmt = (cents) => formatMoney(Math.max(0, Math.round(cents)), moneyFmt, shopCurrency()).replace(/<[^>]*>/g, '');
const postJSON = (path, body) =>
  fetch(`${routeRoot()}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }).then((r) => r.json());
const getCart = () => fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
const cartSections = () => [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
const codeKey = (c) => String(c || '').trim().toUpperCase();

/** the cart's codes + new ones (no duplicates, the shopper's own codes kept) */
function mergeCodes(cart, add) {
  const out = [];
  for (const c of [...((cart && cart.discount_codes) || []).map((d) => d.code), ...add]) if (c && !out.some((x) => codeKey(x) === codeKey(c))) out.push(c);
  return out;
}

/** add lines (+ set the cart's code list) and let Horizon open the drawer (as assets/sb-gp-product.js does) */
async function addLines(source, items, discount, productId) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })), promise: deferred.promise }));
  const sections = cartSections().join(',');
  try {
    const added = await postJSON('cart/add.js', { items, sections: discount ? undefined : sections });
    if (added && added.status) throw new Error(added.description || added.message || 'Cannot add product to cart');
    let response = added;
    if (discount) response = await postJSON('cart/update.js', { discount: discount.join(','), sections });
    const cart = discount && response && response.token ? response : await getCart();
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'product-form-component', sourceId: source.id || 'sb-store-offer', itemCount: items.reduce((s, it) => s + it.quantity, 0), productId: String(productId), sections: response && response.sections, didError: false } });
    return { ok: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    source.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID', detail: { description: message } }));
    const cart = await getCart().catch(() => ({ items: [] }));
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'product-form-component', sourceId: source.id || 'sb-store-offer' } });
    return { ok: false, message };
  }
}

/** variants of the page product (sticky bar's data; quantity breaks carry their own) */
function pageVariants(section) {
  const el = section.querySelector('script[data-sb-satc-json], script[data-sb-qb-json]');
  try { return (JSON.parse(el ? el.textContent : '{}').variants) || []; } catch { return []; }
}
/** the variant the page shows: Horizon's picker (checked radios), else the form's id */
function pageVariant(section) {
  const variants = pageVariants(section);
  const picker = section.querySelector('variant-picker');
  if (picker) {
    const opts = [...picker.querySelectorAll('fieldset, .variant-option')].map((fs) => { const c = fs.querySelector('input[type="radio"]:checked'); return c ? c.value : null; });
    if (opts.length && opts.every(Boolean)) { const v = variants.find((x) => x.options.every((o, i) => o === opts[i])); if (v) return v; }
  }
  const id = Number((section.querySelector('form[data-type="add-to-cart-form"] input[name="id"], .buy-buttons-block input[name="id"]') || {}).value || 0);
  return variants.find((x) => x.id === id) || variants.find((x) => x.available) || variants[0] || null;
}

// ---- quantity breaks
function qbVariants(box) {
  const el = box.querySelector('script[data-sb-qb-json]');
  try { return JSON.parse(el ? el.textContent : '{}'); } catch { return {}; }
}
function qbUnitVariant(data, unit) {
  const sels = [...unit.querySelectorAll('select[data-opt]')];
  return (data.variants || []).find((v) => sels.every((s) => v.options[Number(s.dataset.opt)] === s.value)) || null;
}
function qbUnits(box, tier, fallback) {
  const data = qbVariants(box);
  const qty = Number(tier.dataset.qty || 1);
  const rows = [...tier.querySelectorAll('[data-sb-qb-unit]')];
  const list = [];
  for (let k = 0; k < qty; k++) list.push((rows[k] && qbUnitVariant(data, rows[k])) || fallback);
  return list.filter(Boolean);
}
function qbRefresh(box, section) {
  const data = qbVariants(box);
  if (!data.variants) return;
  const current = pageVariant(section);
  for (const tier of box.querySelectorAll('.sb-qb__tier')) {
    const units = qbUnits(box, tier, current);
    if (!units.length) continue;
    const pct = Number(tier.dataset.percent || 0);
    const amount = Number(tier.dataset.amount || 0);
    const full = units.reduce((s, v) => s + v.price, 0);
    const price = Math.max(0, Math.round((full * (100 - pct)) / 100 - amount));
    const compare = units.reduce((s, v) => s + (data.compareBasis === 'compare_at' && v.compare_at_price > v.price ? v.compare_at_price : v.price), 0);
    const pe = tier.querySelector('[data-sb-qb-price]');
    const ce = tier.querySelector('[data-sb-qb-compare]');
    if (pe) pe.textContent = fmt(price);
    if (ce) { ce.textContent = fmt(compare); ce.hidden = !(compare > price); }
  }
}
function qbFollow(box, v) {
  if (!v) return;
  for (const sel of box.querySelectorAll('[data-sb-qb-unit] select[data-opt]')) {
    const val = v.options[Number(sel.dataset.opt)];
    if ([...sel.options].some((o) => o.value === val)) sel.value = val;
  }
}
function qbNotice(box, text) {
  const n = box.querySelector('[data-sb-qb-notice]');
  if (!n) return;
  n.textContent = text || '';
  n.hidden = !text;
}
async function qbAdd(box, section, button) {
  const tier = box.querySelector('.sb-qb__tier.is-selected') || box.querySelector('.sb-qb__tier');
  if (!tier) return;
  const units = qbUnits(box, tier, pageVariant(section));
  if (!units.length || units.some((v) => !v.available)) { qbNotice(box, 'Sorry, this is not currently available.'); return; }
  qbNotice(box, '');
  const groups = new Map();
  for (const v of units) groups.set(v.id, (groups.get(v.id) || 0) + 1);
  const items = [...groups.entries()].map(([id, quantity]) => ({ id, quantity }));
  const codes = String(tier.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean);
  let discount = null;
  if (codes.length) {
    discount = mergeCodes(await getCart().catch(() => null), codes);
    if (discount.length > MAX_CODES) { qbNotice(box, 'This offer cannot be combined with the other offers in your cart.'); return; }
  }
  button.setAttribute('aria-busy', 'true');
  const r = await addLines(button, items, discount, qbVariants(box).productId);
  button.removeAttribute('aria-busy');
  if (!r.ok && r.message) qbNotice(box, r.message);
}

// ---- the page's offers + add to cart routing
const lastOffer = new WeakMap();
function sectionOf(el) { return el.closest('.shopify-section') || document; }
function activeOffer(section) {
  const picked = lastOffer.get(section);
  if (picked && picked.isConnected) return picked;
  return section.querySelector('[data-sb-offer="deal"]') || section.querySelector('[data-sb-offer="qb"]');
}
/** the page's variant changed (Horizon picker / sticky dropdown): offers follow */
function followVariant(section) {
  const v = pageVariant(section);
  if (!v) return;
  for (const box of section.querySelectorAll('[data-sb-qb][data-managed]')) { qbFollow(box, v); qbRefresh(box, section); }
  for (const sel of section.querySelectorAll('.sb-store-deal select[data-sb-gp-variant-select]')) {
    if (sel.value !== String(v.id)) { sel.value = String(v.id); sel.dispatchEvent(new Event('change', { bubbles: true })); }
  }
  syncSticky(section, v);
}
function initOffers() {
  if (!document.querySelector('[data-sb-offer], [data-sb-satc]')) return;
  // a pick inside an offer makes it the one that ADD TO CART adds
  const mark = (e) => { const o = e.target instanceof Element ? e.target.closest('[data-sb-offer]') : null; if (o) lastOffer.set(sectionOf(o), o); };
  document.addEventListener('pointerdown', mark, true);
  document.addEventListener('change', (e) => {
    mark(e);
    const t = e.target;
    if (!(t instanceof Element)) return;
    const box = t.closest('[data-sb-qb][data-managed]');
    if (box && t.matches('select[data-opt]')) qbRefresh(box, sectionOf(box));
    if (box && t.matches('input[type="radio"]')) qbRefresh(box, sectionOf(box));
    if (t.matches('variant-picker input[type="radio"]')) {
      const section = sectionOf(t);
      requestAnimationFrame(() => followVariant(section));
    }
  });
  // Horizon's ADD TO CART (and the sticky bar, which presses it): route to the active offer
  document.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('.product-information .buy-buttons-block .add-to-cart-button') : null;
    if (!btn) return;
    const section = sectionOf(btn);
    const offer = activeOffer(section);
    if (!offer) return;
    if (offer.matches('[data-sb-offer="deal"]')) {
      const hidden = offer.querySelector('[data-sb-gp-atc]');
      if (!hidden || !offer.querySelector('[data-sb-gp-kaching]')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      hidden.click();
    } else if (offer.matches('[data-managed]')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      qbAdd(offer, section, btn);
    }
  }, true);
  for (const box of document.querySelectorAll('[data-sb-qb][data-managed]')) qbRefresh(box, sectionOf(box));
}

// ---- sticky bar
function syncSticky(section, v) {
  const bar = section.querySelector('[data-sb-satc]') || document.querySelector(`[data-sb-satc][data-section="${section.id}"]`);
  if (!bar || !v) return;
  const pe = bar.querySelector('[data-sb-satc-price]');
  const ce = bar.querySelector('[data-sb-satc-compare]');
  const se = bar.querySelector('[data-sb-satc-save]');
  if (pe) pe.textContent = fmt(v.price);
  if (ce) { ce.textContent = fmt(v.compare_at_price); ce.hidden = !(v.compare_at_price > v.price); }
  if (se) {
    se.hidden = !(v.compare_at_price > v.price);
    const pct = se.querySelector('[data-sb-satc-pct]');
    if (pct && v.compare_at_price > v.price) pct.textContent = String(Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price));
  }
  const img = bar.querySelector('[data-sb-satc-img]');
  if (img && v.image) { img.removeAttribute('srcset'); img.src = v.image; }
  const sel = bar.querySelector('[data-sb-satc-select]');
  if (sel) sel.value = String(v.id);
}
/** pick variant v on the page: Horizon's picker radios when there is one, else the offers follow directly */
function pickVariant(section, v) {
  const picker = section.querySelector('variant-picker');
  if (picker) {
    const sets = [...picker.querySelectorAll('fieldset, .variant-option')];
    sets.forEach((fs, i) => {
      const input = [...fs.querySelectorAll('input[type="radio"]')].find((r) => r.value === v.options[i]);
      if (input && !input.checked) input.click();
    });
    return;
  }
  for (const input of section.querySelectorAll('.buy-buttons-block input[name="id"]')) input.value = String(v.id);
  for (const box of section.querySelectorAll('[data-sb-qb][data-managed]')) { qbFollow(box, v); qbRefresh(box, section); }
  for (const sel of section.querySelectorAll('.sb-store-deal select[data-sb-gp-variant-select]')) { sel.value = String(v.id); sel.dispatchEvent(new Event('change', { bubbles: true })); }
  syncSticky(section, v);
}
function initSticky() {
  for (const bar of document.querySelectorAll('[data-sb-satc]')) {
    const section = sectionOf(bar);
    if (bar.dataset.moneyFormat) moneyFmt = bar.dataset.moneyFormat;
    bar.dataset.section = section.id || '';
    const btn = () => section.querySelector('.buy-buttons-block .add-to-cart-button');
    const target = btn();
    if (!target) continue;
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) bar.dataset.active = String(!en.isIntersecting);
      const on = bar.dataset.active === 'true';
      for (const b of bar.querySelectorAll('button, select')) b.tabIndex = on ? 0 : -1;
      bar.querySelector('.sb-satc__bar')?.setAttribute('aria-hidden', String(!on));
    });
    io.observe(target);
    bar.querySelector('[data-sb-satc-btn]')?.addEventListener('click', () => { const b = btn(); if (b) b.click(); });
    bar.querySelector('[data-sb-satc-select]')?.addEventListener('change', (e) => {
      const v = pageVariants(section).find((x) => String(x.id) === e.target.value);
      if (v) pickVariant(section, v);
    });
  }
}

/* ---------- scratch pop-up "SHOP NOW": the code joins the cart's own codes (a /discount/ link would replace them) */
function initScratchApply() {
  document.addEventListener('click', async (e) => {
    const a = e.target instanceof Element ? e.target.closest('[data-sb-scratch-apply]') : null;
    if (!a) return;
    const code = (document.getElementById('SbScratch') || {}).dataset?.code;
    if (!code) return;
    e.preventDefault();
    try {
      const cart = await getCart();
      const codes = mergeCodes(cart, [code]);
      await postJSON('cart/update.js', { discount: codes.slice(-MAX_CODES).join(',') });
      window.location.href = a.dataset.redirect || `${routeRoot()}collections/all`;
    } catch {
      window.location.href = a.getAttribute('href');
    }
  });
}

/* ---------- cart drawer heading: "1 item" / "n items" (the badge's count, read by sb-store-chrome.css) ---------- */
function initCartCount() {
  const sync = () => document.querySelectorAll('#cart-drawer .theme-drawer__badge').forEach((b) => {
    const n = b.textContent.replace(/\D+/g, '');
    if (b.dataset.count !== n) b.dataset.count = n;
  });
  sync();
  const drawer = document.getElementById('cart-drawer');
  if (drawer) new MutationObserver(sync).observe(drawer, { childList: true, subtree: true, characterData: true });
}

/* ---------- transplanted pages (the club pages): the source's scroll reveal, ".reveal" gets ".in" on screen ---------- */
function initReveals() {
  const els = document.querySelectorAll('.reveal:not(.in)');
  if (!els.length) return;
  if (!('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('in')); return; }
  const io = new IntersectionObserver((entries) => {
    for (const en of entries) if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); }
  }, { rootMargin: '0px 0px -8% 0px' });
  els.forEach((e) => io.observe(e));
}

/* (the source's countdown bar, sections/sb-store-countdown-bar.liquid, and its script were removed in the Google
   Merchant Center clean-up, 2026-10-23: OWNER-STEPS E6) */

function init() {
  initReveals();
  initCartCount();
  initQtyBreaks();
  initSliders();
  addDrawerAccount();
  initTop();
  initScratch();
  initPredictive();
  initOffers();
  initSticky();
  initScratchApply();
  // the menu drawer is hydrated by Horizon after load: add the row again when it is (re)built
  document.addEventListener('click', (e) => { if (e.target instanceof Element && e.target.closest('.header__icon--menu')) addDrawerAccount(); }, true);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
